import { createSignal, onCleanup } from "solid-js";
import { tgHapticImpact } from "./telegram";

/** Layout rects relative to the list top (transform-independent). */
type RelRect = { top: number; height: number; mid: number };

/**
 * Pointer-based list reordering driven by a grip handle.
 *
 * Design goals vs the previous version:
 * - Clear insert-line indicator (primary UX)
 * - rAF + imperative transforms (no list re-render storm / desktop freezes)
 * - Gap opens via neighbor shifts so the drop target is obvious
 * - touch-action only on the handle so page scroll stays free
 */
export function createListDrag(opts: {
  onReorder: (from: number, to: number) => void;
}) {
  const [activeIndex, setActiveIndex] = createSignal<number | null>(null);
  const [insertIndex, setInsertIndex] = createSignal<number | null>(null);
  const [dragging, setDragging] = createSignal(false);

  const itemEls = new Map<number, HTMLElement>();
  let listEl: HTMLElement | null = null;
  let indicatorEl: HTMLElement | null = null;

  let pointerId: number | null = null;
  let handleEl: HTMLElement | null = null;
  let startClientY = 0;
  let fromIndex = -1;
  let currentTo = -1;
  let lastHapticTo: number | null = null;
  let dragHeight = 0;
  let gapPx = 12;
  let relRects: RelRect[] = [];
  let listTop = 0;
  let rafId: number | null = null;
  let pendingY: number | null = null;
  let scrollParent: HTMLElement | Window | null = null;
  let scrollPosAtStart = 0;
  let autoScrollDir = 0;
  let autoScrollRaf: number | null = null;
  let active = false;

  function getScrollPos(): number {
    if (scrollParent === window) {
      return window.scrollY || document.documentElement.scrollTop || 0;
    }
    if (scrollParent instanceof HTMLElement) return scrollParent.scrollTop;
    return 0;
  }

  function syncItemsFromDom() {
    if (!listEl) return;
    itemEls.clear();
    const nodes = listEl.querySelectorAll<HTMLElement>("[data-drag-index]");
    for (const el of nodes) {
      const idx = Number(el.dataset.dragIndex);
      if (Number.isFinite(idx)) itemEls.set(idx, el);
    }
  }

  function resetVisuals() {
    for (const el of itemEls.values()) {
      el.style.transform = "";
      el.style.transition = "";
      el.style.zIndex = "";
      el.style.pointerEvents = "";
      el.style.opacity = "";
      el.classList.remove("is-dragging", "is-drag-shifting");
    }
    if (indicatorEl) {
      indicatorEl.style.opacity = "0";
      indicatorEl.style.transform = "translate3d(0, 0, 0) scaleX(0.55)";
      indicatorEl.hidden = true;
    }
  }

  function reset() {
    if (rafId != null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    stopAutoScroll();
    resetVisuals();
    active = false;
    pointerId = null;
    handleEl = null;
    startClientY = 0;
    fromIndex = -1;
    currentTo = -1;
    lastHapticTo = null;
    dragHeight = 0;
    relRects = [];
    listTop = 0;
    pendingY = null;
    scrollParent = null;
    scrollPosAtStart = 0;
    setActiveIndex(null);
    setInsertIndex(null);
    setDragging(false);
  }

  function findScrollParent(el: HTMLElement | null): HTMLElement | Window {
    let node: HTMLElement | null = el;
    while (node && node !== document.body) {
      const style = getComputedStyle(node);
      const oy = style.overflowY;
      if (
        (oy === "auto" || oy === "scroll" || oy === "overlay") &&
        node.scrollHeight > node.clientHeight + 1
      ) {
        return node;
      }
      node = node.parentElement;
    }
    return window;
  }

  /**
   * Capture layout geometry once at drag start (before transforms).
   * Midpoints stay stable relative to the list; only listTop moves with scroll.
   */
  function captureLayout() {
    if (!listEl) return false;
    const listRect = listEl.getBoundingClientRect();
    listTop = listRect.top;
    const n = itemEls.size;
    relRects = [];
    let prevBottom: number | null = null;
    for (let i = 0; i < n; i++) {
      const el = itemEls.get(i);
      if (!el) {
        relRects.push({ top: 0, height: 0, mid: 0 });
        continue;
      }
      const r = el.getBoundingClientRect();
      const top = r.top - listTop;
      relRects.push({
        top,
        height: r.height,
        mid: top + r.height / 2,
      });
      if (prevBottom != null) {
        gapPx = Math.max(0, r.top - prevBottom);
      }
      prevBottom = r.bottom;
    }
    return relRects.length > 0;
  }

  function refreshListTop() {
    if (!listEl) return;
    listTop = listEl.getBoundingClientRect().top;
  }

  /** Final index after drop (0..n-1), based on layout midpoints of other items. */
  function computeDropIndex(clientY: number): number {
    const n = relRects.length;
    if (n === 0) return 0;
    let before = 0;
    for (let i = 0; i < n; i++) {
      if (i === fromIndex) continue;
      const midClient = listTop + relRects[i]!.mid;
      if (clientY > midClient) before++;
    }
    return Math.max(0, Math.min(n - 1, before));
  }

  function shiftFor(i: number, from: number, to: number): number {
    if (i === from) return 0;
    const step = dragHeight + gapPx;
    if (from < to) {
      if (i > from && i <= to) return -step;
    } else if (from > to) {
      if (i >= to && i < from) return step;
    }
    return 0;
  }

  /** Insert-line Y relative to list top (center of the opened gap). */
  function indicatorOffsetY(from: number, to: number): number {
    const step = dragHeight + gapPx;
    if (from === to) {
      const r = relRects[from]!;
      return r.top + r.height / 2;
    }
    if (from < to) {
      // Gap opens just below item `to` after it shifts up
      const r = relRects[to]!;
      const visualBottom = r.top + r.height - step;
      return visualBottom + gapPx / 2;
    }
    // Gap opens just above item `to` after it shifts down
    const r = relRects[to]!;
    const visualTop = r.top + step;
    return visualTop - gapPx / 2;
  }

  function scrollCompensation(): number {
    return getScrollPos() - scrollPosAtStart;
  }

  function applyFrame(clientY: number) {
    if (!active || fromIndex < 0) return;
    refreshListTop();
    const to = computeDropIndex(clientY);
    currentTo = to;

    const dragged = itemEls.get(fromIndex);
    if (dragged) {
      const delta = clientY - startClientY + scrollCompensation();
      dragged.style.transform = `translate3d(0, ${delta}px, 0) scale(1.03)`;
    }

    for (const [i, el] of itemEls) {
      if (i === fromIndex) continue;
      const shift = shiftFor(i, fromIndex, to);
      el.style.transform = shift
        ? `translate3d(0, ${shift}px, 0)`
        : "translate3d(0, 0, 0)";
    }

    if (indicatorEl) {
      const y = indicatorOffsetY(fromIndex, to);
      indicatorEl.hidden = false;
      indicatorEl.style.opacity = fromIndex === to ? "0" : "1";
      indicatorEl.style.transform = `translate3d(0, ${y}px, 0) scaleX(${
        fromIndex === to ? 0.55 : 1
      })`;
    }

    if (to !== lastHapticTo) {
      lastHapticTo = to;
      if (to !== fromIndex) tgHapticImpact("soft");
      setInsertIndex(to);
    }
  }

  function scheduleFrame(clientY: number) {
    pendingY = clientY;
    if (rafId != null) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      if (pendingY == null || !active) return;
      const y = pendingY;
      pendingY = null;
      applyFrame(y);
    });
  }

  function stopAutoScroll() {
    autoScrollDir = 0;
    if (autoScrollRaf != null) {
      cancelAnimationFrame(autoScrollRaf);
      autoScrollRaf = null;
    }
  }

  function tickAutoScroll() {
    autoScrollRaf = null;
    if (!autoScrollDir || !active) return;
    const speed = autoScrollDir * 12;
    if (scrollParent === window) {
      window.scrollBy(0, speed);
    } else if (scrollParent instanceof HTMLElement) {
      scrollParent.scrollTop += speed;
    }
    if (pendingY != null) applyFrame(pendingY);
    autoScrollRaf = requestAnimationFrame(tickAutoScroll);
  }

  function updateAutoScroll(clientY: number) {
    const edge = 56;
    let dir = 0;
    if (scrollParent === window) {
      if (clientY < edge) dir = -1;
      else if (clientY > window.innerHeight - edge) dir = 1;
    } else if (scrollParent instanceof HTMLElement) {
      const r = scrollParent.getBoundingClientRect();
      if (clientY < r.top + edge) dir = -1;
      else if (clientY > r.bottom - edge) dir = 1;
    }
    if (dir === autoScrollDir) return;
    autoScrollDir = dir;
    if (!dir) {
      if (autoScrollRaf != null) {
        cancelAnimationFrame(autoScrollRaf);
        autoScrollRaf = null;
      }
      return;
    }
    if (autoScrollRaf == null) {
      autoScrollRaf = requestAnimationFrame(tickAutoScroll);
    }
  }

  function onHandlePointerDown(index: number, e: PointerEvent) {
    if (e.button !== 0 || active) return;
    if (!listEl) return;

    e.preventDefault();
    e.stopPropagation();

    syncItemsFromDom();
    const row = itemEls.get(index);
    if (!row) return;

    const target = e.currentTarget as HTMLElement;
    try {
      target.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }

    handleEl = target;
    pointerId = e.pointerId;
    fromIndex = index;
    currentTo = index;
    lastHapticTo = index;
    startClientY = e.clientY;
    scrollParent = findScrollParent(listEl);
    scrollPosAtStart = getScrollPos();
    active = true;

    if (!captureLayout() || !relRects[index]) {
      reset();
      return;
    }
    dragHeight = relRects[index]!.height;

    setActiveIndex(index);
    setInsertIndex(index);
    setDragging(true);

    for (const [i, el] of itemEls) {
      el.classList.toggle("is-dragging", i === index);
      el.classList.toggle("is-drag-shifting", i !== index);
      el.style.transition = "none";
      if (i === index) {
        el.style.zIndex = "40";
        el.style.pointerEvents = "none";
        el.style.opacity = "0.98";
        el.style.transform = "translate3d(0, 0, 0) scale(1.03)";
      } else {
        el.style.transform = "translate3d(0, 0, 0)";
      }
    }

    requestAnimationFrame(() => {
      if (!active) return;
      for (const [i, el] of itemEls) {
        if (i === fromIndex) {
          el.style.transition =
            "box-shadow 0.2s var(--ease-luxury), opacity 0.15s ease";
        } else {
          el.style.transition = "transform 0.22s var(--ease-luxury)";
        }
      }
      if (indicatorEl) {
        indicatorEl.hidden = false;
        indicatorEl.style.opacity = "0";
        indicatorEl.style.transition =
          "opacity 0.18s var(--ease-luxury), transform 0.18s var(--ease-luxury)";
      }
    });

    tgHapticImpact("medium");
  }

  function onHandlePointerMove(e: PointerEvent) {
    if (!active || pointerId !== e.pointerId || fromIndex < 0) return;
    scheduleFrame(e.clientY);
    updateAutoScroll(e.clientY);
  }

  function finish(commit: boolean) {
    if (!active) return;
    const f = fromIndex;
    const t = currentTo;
    const capturer = handleEl;
    const pid = pointerId;

    // Mark inactive first so duplicate pointerup/window handlers no-op
    active = false;
    stopAutoScroll();
    if (rafId != null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }

    if (capturer && pid != null) {
      try {
        capturer.releasePointerCapture(pid);
      } catch {
        /* ignore */
      }
    }

    if (commit && f >= 0 && t >= 0 && f !== t) {
      resetVisuals();
      opts.onReorder(f, t);
      tgHapticImpact("light");
      pointerId = null;
      handleEl = null;
      fromIndex = -1;
      currentTo = -1;
      lastHapticTo = null;
      relRects = [];
      pendingY = null;
      scrollParent = null;
      setActiveIndex(null);
      setInsertIndex(null);
      setDragging(false);
      return;
    }

    // Cancel or no-op move
    pointerId = null;
    handleEl = null;
    fromIndex = -1;
    currentTo = -1;
    lastHapticTo = null;
    relRects = [];
    pendingY = null;
    scrollParent = null;
    resetVisuals();
    setActiveIndex(null);
    setInsertIndex(null);
    setDragging(false);
  }

  function onHandlePointerUp(e: PointerEvent) {
    if (pointerId !== e.pointerId) return;
    finish(true);
  }

  function onHandlePointerCancel(e: PointerEvent) {
    if (pointerId !== e.pointerId) return;
    finish(false);
  }

  function onWindowPointerUp(e: PointerEvent) {
    if (!active || pointerId == null || e.pointerId !== pointerId) return;
    finish(true);
  }

  if (typeof window !== "undefined") {
    window.addEventListener("pointerup", onWindowPointerUp);
    window.addEventListener("pointercancel", onWindowPointerUp);
    onCleanup(() => {
      window.removeEventListener("pointerup", onWindowPointerUp);
      window.removeEventListener("pointercancel", onWindowPointerUp);
      reset();
    });
  }

  function setListRef(el: HTMLElement | null) {
    listEl = el;
  }

  function setIndicatorRef(el: HTMLElement | null) {
    indicatorEl = el;
    if (el) {
      el.hidden = true;
      el.style.opacity = "0";
    }
  }

  function setItemRef(index: number, el: HTMLElement | null) {
    if (el) itemEls.set(index, el);
    else itemEls.delete(index);
  }

  function handleProps(index: number) {
    return {
      "data-drag-handle": "true" as const,
      class: "drag-handle",
      style: { "touch-action": "none" as const },
      onPointerDown: (e: PointerEvent) => onHandlePointerDown(index, e),
      onPointerMove: onHandlePointerMove,
      onPointerUp: onHandlePointerUp,
      onPointerCancel: onHandlePointerCancel,
    };
  }

  function itemClass(index: number) {
    const parts = ["drag-item"];
    if (activeIndex() === index) parts.push("is-dragging");
    return parts.join(" ");
  }

  return {
    dragging,
    activeIndex,
    insertIndex,
    isDragging: () => dragging(),
    setListRef,
    setIndicatorRef,
    setItemRef,
    handleProps,
    itemClass,
  };
}
